import React from 'react';
import {
  Box, Paper, Typography, TextField, Button, Skeleton, Snackbar, Alert, Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

import { PageHeader, ErrorState, PermissionState } from '../ui';
import { radius } from '../../themes/smartinventoryTheme';
import useGASettingsPage from './useGASettingsPage';

const GASettings = () => {
  const p = useGASettingsPage();
  const closeSnackbar = () => p.setSnackbar((s) => ({ ...s, open: false }));

  if (p.status === 'loading') {
    return (
      <Box>
        <Skeleton variant="text" width={240} height={40} />
        <Skeleton variant="rounded" height={180} sx={{ mt: 2, borderRadius: `${radius.xl}px` }} />
      </Box>
    );
  }

  if (p.status === 'error') {
    return (
      <ErrorState
        title={p.t("Couldn't load platform settings", { defaultValue: "Couldn't load platform settings" })}
        description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
        request={p.errorInfo?.request}
        retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
        onRetry={p.fetchSettings}
      />
    );
  }

  if (p.status === 'permission') {
    return (
      <PermissionState
        title={p.t("You don't have access to Platform settings", { defaultValue: "You don't have access to Platform settings" })}
        description={p.t('Ask a platform operator for access.', { defaultValue: 'Ask a platform operator for access.' })}
      />
    );
  }

  return (
    <Box>
      <PageHeader
        title={p.t('Platform settings', { defaultValue: 'Platform settings' })}
        subtitle={p.t('Rules that apply to every company on the platform', { defaultValue: 'Rules that apply to every company on the platform' })}
      />

      <Paper variant="outlined" sx={{ p: 3, maxWidth: 560 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {p.t('Company creation', { defaultValue: 'Company creation' })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {p.t('How many companies a single user may own. Every company where the user is an Admin counts, including ones set up by the Global Admin; being a plain member does not. Set 0 to turn self-serve creation off.', { defaultValue: 'How many companies a single user may own. Every company where the user is an Admin counts, including ones set up by the Global Admin; being a plain member does not. Set 0 to turn self-serve creation off.' })}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label={p.t('Maximum companies per user', { defaultValue: 'Maximum companies per user' })}
            type="number"
            value={p.maxCompaniesInput}
            onChange={(e) => p.setMaxCompaniesInput(e.target.value)}
            error={Boolean(p.fieldError)}
            helperText={p.fieldError || ' '}
            disabled={p.saving}
            slotProps={{ htmlInput: { min: 0, step: 1, inputMode: 'numeric' } }}
            sx={{ minWidth: 260 }}
          />
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={p.save}
            disabled={p.saving}
            sx={{ mt: { sm: 1 } }}
          >
            {p.saving ? p.t('Saving...', { defaultValue: 'Saving...' }) : p.t('Save')}
          </Button>
        </Stack>
      </Paper>

      <Snackbar open={p.snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert severity={p.snackbar.severity} onClose={closeSnackbar}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GASettings;
